import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef } from './tipos';
import { dataBr, din, junta, num, op } from './util';
import { textoIdade, agendarRemedio } from './saude';
import { temRotinas, tirarRotinas } from './agenda';
import { addDays, today, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';

const ESPECIE_ICONE: Record<string, string> = { 'Cachorro': '🐶', 'Gato': '🐱', 'Pássaro': '🐦', 'Peixe': '🐠', 'Coelho': '🐰', 'Roedor': '🐹', 'Réptil': '🦎' };

const pet: Colecao = {
  id: 'pet', nome: N_('Pets'), um: N_('Pet'), icone: r => ESPECIE_ICONE[r.c.especie] ?? '🐾',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'especie', r: N_('Espécie'), t: 'opcao', op: [N_('Cachorro'), N_('Gato'), N_('Pássaro'), N_('Peixe'), N_('Coelho'), N_('Roedor'), N_('Réptil'), N_('Outro')] },
    { k: 'raca', r: N_('Raça'), t: 'texto' },
    { k: 'nasc', r: N_('Nascimento'), t: 'data' },
    { k: 'peso', r: N_('Peso'), t: 'numero', un: 'kg' },
    { k: 'chip', r: N_('Microchip'), t: 'texto' },
    { k: 'vet', r: N_('Veterinário'), t: 'texto' },
    { k: 'telVet', r: N_('Telefone do veterinário'), t: 'telefone' },
    { k: 'endVet', r: N_('Endereço do veterinário'), t: 'endereco' },
    { k: 'notas', r: N_('Observações (alergias, ração...)'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(op(r.c.especie), r.c.raca, textoIdade(r.c.nasc), num(r.c.peso) ? `${num(r.c.peso)} kg` : ''), ordem: 'titulo', anexos: true,
  acoes: (r, ctx) => [acaoRelatorioPdf(() => relatorioPet(ctx, r))],   // 0.65 (B15)
  vazio: N_('Cadastre cada pet: carteira de vacinação, vermífugos, remédios e pet shop ficam dentro dele.'),
};

const vacinaPet: Colecao = {
  id: 'vacinaPet', pai: 'pet', nome: N_('Vacinas'), um: N_('Vacina'), icone: '💉',
  campos: [
    { k: 'nome', r: N_('Vacina'), t: 'texto', ph: N_('Ex.: V10, antirrábica') },
    { k: 'data', r: N_('Data da aplicação'), t: 'data' },
    { k: 'proxima', r: N_('Próxima dose'), t: 'data', aviso: [15, 3] },
    { k: 'vet', r: N_('Veterinário ou clínica'), t: 'texto' },
    { k: 'lote', r: N_('Lote'), t: 'texto' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(r.c.data && t('aplicada em {d}', { d: dataBr(r.c.data) }), r.c.vet), data: 'proxima', anexos: true,
  novo: () => ({ data: today() }),
  ajustar: (c, k) => (k === 'data' && c.data && !c.proxima ? { ...c, proxima: addDays(c.data, 365) } : c),
  agenda: (r, ctx) => (r.c.proxima ? { start: r.c.proxima, title: t('Vacina {nome}: {pet}', { nome: r.c.nome, pet: ctx.porId(r.pai)?.c.nome ?? '' }) } : null),
};

const cuidadoPet: Colecao = {
  id: 'cuidadoPet', pai: 'pet', nome: N_('Vermífugos e remédios'), um: N_('Vermífugo ou remédio'), icone: r => (r.c.tipo === 'Vermífugo' ? '🪱' : r.c.tipo === 'Antipulgas' ? '🦟' : '💊'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Vermífugo'), N_('Antipulgas'), N_('Remédio contínuo'), N_('Remédio'), N_('Outro')] },
    { k: 'nome', r: N_('Produto'), t: 'texto' },
    { k: 'dose', r: N_('Dose'), t: 'texto' },
    { k: 'data', r: N_('Última aplicação'), t: 'data' },
    { k: 'intervalo', r: N_('Repetir a cada quantos dias'), t: 'numero' },
    { k: 'proxima', r: N_('Próxima'), t: 'data', aviso: [3, 0] },
    { k: 'horarios', r: N_('Horários (remédio diário)'), t: 'texto', ph: N_('Ex.: 08:00 e 20:00'), mostrar: c => c.tipo === 'Remédio contínuo' || c.tipo === 'Remédio' },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.nome), sub: r => junta(r.c.dose, num(r.c.intervalo) && t('a cada {n} dias', { n: num(r.c.intervalo) }), temRotinas(r) && '⏰'),
  data: 'proxima', anexos: true,
  ajustar: (c, k) => ((k === 'data' || k === 'intervalo') && c.data && num(c.intervalo) ? { ...c, proxima: addDays(c.data, num(c.intervalo)) } : c),
  agenda: (r, ctx) => (r.c.proxima ? { start: r.c.proxima, title: junta(r.c.nome || op(r.c.tipo), ctx.porId(r.pai)?.c.nome) } : null),
  acoes: (r, ctx) => (r.c.horarios ? [
    { label: '⏰ ' + t('Colocar na linha do tempo com alarme'), onPress: () => agendarRemedio('familia', { ...r, c: { ...r.c, alarme: true, cada: 1, inicio: r.c.data || today() } }, ctx.porId(r.pai)?.c.nome ?? '') },
    ...(temRotinas(r) ? [{ label: t('Tirar alarmes do My Planner'), onPress: () => tirarRotinas('familia', r) }] : []),
  ] : []),
};

const petshop: Colecao = {
  id: 'petshop', pai: 'pet', nome: N_('Pet shop'), um: N_('Agendamento no pet shop'), icone: '🛁',
  campos: [
    { k: 'servico', r: N_('Serviço'), t: 'opcao', op: [N_('Banho'), N_('Tosa'), N_('Banho e tosa'), N_('Consulta'), N_('Hotel'), N_('Adestramento'), N_('Outro')] },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [1] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'local', r: N_('Pet shop ou clínica'), t: 'texto' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
  ],
  titulo: r => op(r.c.servico) || t('Pet shop'), sub: (r, ctx) => junta(horaTela(r.c.hora), r.c.local, r.c.valor !== '' && r.c.valor != null && din(r.c.valor, ctx.moeda)), data: 'data',
  agenda: (r, ctx) => ({ time: r.c.hora || null, title: junta(op(r.c.servico), ctx.porId(r.pai)?.c.nome), notes: junta(r.c.local, r.c.telefone) }),
};

const crianca: Colecao = {
  id: 'crianca', nome: N_('Crianças'), um: N_('Criança'), icone: '🧒',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'nasc', r: N_('Nascimento'), t: 'data' },
    { k: 'escola', r: N_('Escola'), t: 'texto' },
    { k: 'turma', r: N_('Série e turma'), t: 'texto' },
    { k: 'professor', r: N_('Professor(a)'), t: 'texto' },
    { k: 'telEscola', r: N_('Telefone da escola'), t: 'telefone' },
    { k: 'endEscola', r: N_('Endereço da escola'), t: 'endereco' },
    { k: 'pediatra', r: N_('Pediatra'), t: 'texto' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(textoIdade(r.c.nasc), r.c.escola, r.c.turma), ordem: 'titulo', anexos: true,
  acoes: (r, ctx) => [acaoRelatorioPdf(() => relatorioCrianca(ctx, r))],   // 0.65 (B15)
  vazio: N_('Cadastre as crianças: provas, reuniões, consultas e vacinas ficam dentro de cada uma.'),
};

const escola: Colecao = {
  id: 'escola', pai: 'crianca', nome: N_('Escola'), um: N_('Compromisso da escola'), icone: r => (r.c.tipo === 'Prova' ? '📝' : r.c.tipo === 'Reunião' ? '👥' : '🏫'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Prova'), N_('Trabalho'), N_('Reunião'), N_('Passeio'), N_('Apresentação'), N_('Feriado escolar'), N_('Outro')] },
    { k: 'titulo', r: N_('Assunto'), t: 'texto', ph: N_('Ex.: Prova de matemática') },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [2, 0] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'notas', r: N_('O que levar ou estudar'), t: 'longo' },
  ],
  titulo: r => r.c.titulo || op(r.c.tipo), sub: r => junta(op(r.c.tipo), horaTela(r.c.hora)), data: 'data', anexos: true,
  agenda: (r, ctx) => ({ time: r.c.hora || null, title: junta(r.c.titulo || op(r.c.tipo), ctx.porId(r.pai)?.c.nome), notes: r.c.notas || '' }),
};

const saudeCrianca: Colecao = {
  id: 'saudeCrianca', pai: 'crianca', nome: N_('Saúde'), um: N_('Consulta ou vacina'), icone: r => (r.c.tipo === 'Vacina' ? '💉' : r.c.tipo === 'Dentista' ? '🦷' : '🩺'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Consulta'), N_('Vacina'), N_('Exame'), N_('Dentista'), N_('Outro')] },
    { k: 'titulo', r: N_('Descrição'), t: 'texto' },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [2, 0] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'local', r: N_('Profissional ou local'), t: 'texto' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.titulo), sub: r => junta(horaTela(r.c.hora), r.c.local), data: 'data', anexos: true,
  agenda: (r, ctx) => ({ time: r.c.hora || null, title: junta(op(r.c.tipo), r.c.titulo, ctx.porId(r.pai)?.c.nome), notes: r.c.local || '' }),
};

// ─── 0.65 (B15): relatório em PDF do pet e da criança ───
const temV = (x: unknown) => x !== '' && x != null;

function relatorioPet(ctx: Ctx, p: Reg) {
  const f = (col: string) => porDataPdf(ctx.filhos(p.id, col), 'data', true);
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: p.c.nome || t('Pet') },
    ...dadosPdf([[t('Espécie'), op(p.c.especie)], [t('Raça'), p.c.raca], [t('Nascimento'), junta(dataBr(p.c.nasc), textoIdade(p.c.nasc))],
      [t('Peso'), num(p.c.peso) ? `${num(p.c.peso)} kg` : ''], [t('Microchip'), p.c.chip], [t('Veterinário'), junta(p.c.vet, p.c.telVet, p.c.endVet)],
      [t('Observações (alergias, ração...)'), p.c.notas]]),
    ...secaoPdf(t('Vacinas'), [t('Vacina'), t('Data da aplicação'), t('Próxima dose'), t('Veterinário ou clínica'), t('Lote')],
      f('vacinaPet').map(r => [r.c.nome || '', dataBr(r.c.data), dataBr(r.c.proxima), r.c.vet || '', r.c.lote || '']), [0.24, 0.16, 0.16, 0.28, 0.16]),
    ...secaoPdf(t('Vermífugos e remédios'), [t('Tipo'), t('Produto'), t('Dose'), t('Última aplicação'), t('Próxima')],
      f('cuidadoPet').map(r => [op(r.c.tipo), r.c.nome || '', r.c.dose || '', dataBr(r.c.data), dataBr(r.c.proxima)]), [0.2, 0.26, 0.18, 0.18, 0.18]),
    ...secaoPdf(t('Pet shop'), [t('Data'), t('Horário'), t('Serviço'), t('Pet shop ou clínica'), t('Valor')],
      f('petshop').map(r => [dataBr(r.c.data), horaTela(r.c.hora), op(r.c.servico), r.c.local || '', temV(r.c.valor) ? din(r.c.valor, ctx.moeda) : '']), [0.15, 0.11, 0.24, 0.32, 0.18]),
  ];
  return { nome: junta(t('Pet'), p.c.nome), blocos: b };
}

function relatorioCrianca(ctx: Ctx, c: Reg) {
  const f = (col: string) => porDataPdf(ctx.filhos(c.id, col), 'data', true);
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: c.c.nome || t('Criança') },
    ...dadosPdf([[t('Nascimento'), junta(dataBr(c.c.nasc), textoIdade(c.c.nasc))], [t('Escola'), junta(c.c.escola, c.c.telEscola)], [t('Série e turma'), c.c.turma],
      [t('Professor(a)'), c.c.professor], [t('Pediatra'), c.c.pediatra], [t('Observações'), c.c.notas]]),
    ...secaoPdf(t('Saúde'), [t('Data'), t('Horário'), t('Tipo'), t('Descrição'), t('Profissional ou local')],
      f('saudeCrianca').map(r => [dataBr(r.c.data), horaTela(r.c.hora), op(r.c.tipo), r.c.titulo || '', r.c.local || '']), [0.14, 0.1, 0.16, 0.32, 0.28]),
    ...secaoPdf(t('Escola'), [t('Data'), t('Horário'), t('Tipo'), t('Assunto')],
      f('escola').map(r => [dataBr(r.c.data), horaTela(r.c.hora), op(r.c.tipo), r.c.titulo || '']), [0.15, 0.11, 0.2, 0.54]),
  ];
  return { nome: junta(t('Criança'), c.c.nome), blocos: b };
}

export const FAMILIA: ModDef = { id: 'familia', colecoes: [pet, vacinaPet, cuidadoPet, petshop, crianca, escola, saudeCrianca] };
